import React, { useEffect, useRef } from 'react';
import { TabListState } from 'react-stately/useTabListState';
import { Node } from '@react-types/shared';
import { TabPanel } from './TabPanel';

interface TabPanelWrapperProps<T> {
  state: TabListState<T>;
  keepMounted?: boolean;
}

/**
 * Component that manages which tab panels should be rendered.
 *
 * When keepMounted is false (default): Only renders the currently selected tab.
 * When keepMounted is true: Renders all tabs that have been visited at least once.
 */
export function TabPanelWrapper<T>({
  state,
  keepMounted = false,
}: TabPanelWrapperProps<T>): JSX.Element {
  // Track which tabs have been mounted at least once
  const mountedTabsRef = useRef<Set<React.Key>>(
    new Set(state.selectedKey ? [state.selectedKey] : [])
  );

  // Add newly selected tab to mounted set when keepMounted is true
  if (keepMounted && state.selectedKey) {
    mountedTabsRef.current.add(state.selectedKey);
  }

  // Reset mounted tabs when keepMounted changes from true to false
  useEffect(() => {
    if (!keepMounted && state.selectedKey) {
      mountedTabsRef.current = new Set([state.selectedKey]);
    }
  }, [keepMounted, state.selectedKey]);

  // Determine which items to render
  const tabs: Node<T>[] = keepMounted
    ? Array.from(state.collection).filter((item) =>
        mountedTabsRef.current.has(item.key)
      )
    : state.selectedItem
    ? [state.selectedItem]
    : [];

  return (
    <>
      {tabs.map((item) => (
        <TabPanel
          key={item.key}
          state={state}
          isSelected={state.selectedKey === item.key}
          isVisible={!keepMounted || state.selectedKey === item.key}
        >
          {item.props.children}
        </TabPanel>
      ))}
    </>
  );
}
